/**
 * Canonical emissions formatting helpers — ENH-02.
 *
 * Rule (ENH-02) : the unit (kg vs t) is dictated by the FY TOTAL emissions :
 *   total >= 10 tCO2e  -> ALL values rendered in tCO2e (never mixed)
 *   total <  10 tCO2e  -> ALL values rendered in kgCO2e
 * Threshold 10t is hardcoded.
 *
 * Fallback (transition) : if `totalEmissionsKg` is null/undefined (legacy
 * call site not yet migrated, or no FY context available), the helper
 * falls back to the per-value behaviour (the value itself compared to the
 * 10t threshold). This guarantees a drop-in replacement of the 3 legacy
 * helpers without any regression while LOT A/B migrate call sites
 * incrementally.
 *
 * Unit form : 'tCO2e' / 'kgCO2e' (NO ₂ subscript) — aligned with the
 * majority of legacy call sites (helpers 1 & 3).
 *
 * Rounding standard (ENH-02) :
 *   - formatEmissions  : 0 décimale (KPI cards / sidebars / table headers)
 *   - formatChartValue : 1 décimale (tooltips chart, plus de précision)
 *
 * Examples :
 *   formatEmissions(5000, 20000)  -> { value: '5',     unit: 'tCO2e'  }  // total=20t ≥10 -> tonnes
 *   formatEmissions(5000, 8000)   -> { value: '5 000', unit: 'kgCO2e' }  // total=8t <10 -> kg
 *   formatEmissions(5000)         -> { value: '5 000', unit: 'kgCO2e' }  // fallback: 5t <10 -> kg
 *   formatEmissions(50000)        -> { value: '50',    unit: 'tCO2e'  }  // fallback: 50t ≥10 -> tonnes
 *   formatEmissions(null)         -> { value: '0',     unit: 'kgCO2e' }
 *   formatEmissions(null, 20000)  -> { value: '0',     unit: 'kgCO2e' }  // null value -> always kg "0"
 */

const THRESHOLD_TONNES = 10;
const LOCALE = 'fr-FR';

const _useTonnes = (valueInKg, totalEmissionsKg) => {
  // ENH-02 mode : total-aware (jamais mixte).
  if (totalEmissionsKg !== null && totalEmissionsKg !== undefined) {
    return (totalEmissionsKg / 1000) >= THRESHOLD_TONNES;
  }
  // Fallback : per-value (legacy helper 1 behaviour).
  return ((valueInKg || 0) / 1000) >= THRESHOLD_TONNES;
};

export const formatEmissions = (valueInKg, totalEmissionsKg) => {
  if (valueInKg === null || valueInKg === undefined) {
    return { value: '0', unit: 'kgCO2e' };
  }
  if (_useTonnes(valueInKg, totalEmissionsKg)) {
    return {
      value: (valueInKg / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 0 }),
      unit: 'tCO2e',
    };
  }
  return {
    value: valueInKg.toLocaleString(LOCALE, { maximumFractionDigits: 0 }),
    unit: 'kgCO2e',
  };
};

export const formatChartValue = (valueInKg, totalEmissionsKg) => {
  if (valueInKg === null || valueInKg === undefined) return '0 kgCO2e';
  if (_useTonnes(valueInKg, totalEmissionsKg)) {
    return `${(valueInKg / 1000).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} tCO2e`;
  }
  return `${valueInKg.toLocaleString(LOCALE, { maximumFractionDigits: 1 })} kgCO2e`;
};


/* ────────────────────────────────────────────────────────────────────────────
 * Lot 1 quinquies — prévisualisation du RAPPORT (ExportPreviewModal).
 * MIROIR de backend/services/report_format.py : la modale prévisualise le PDF,
 * elle en reproduit la règle — PAS la règle ENH-02 du dashboard ci-dessus
 * (kg/t selon le total). Typographie du rapport : point décimal, espace milliers.
 *   D3 : décimales selon l'ordre de grandeur (≥100 → 0, ≥10 → 1, ≥1 → 2, ≥0.01 → 3)
 *   D6 : précision d'un GROUPE = décimales D3 de son total (0 → 0 ; sous 0.01 → 3)
 *   D7 : non nul qui arrondirait à zéro → « < X » ; zéro réel → « 0 »
 *   D8 : ratios d'intensité (tCO2e/ETP, tCO2e/kCHF, kgCO2e/m²) → 2 décimales FIXES
 * Parité verrouillée par emissionsFormat.report.test.js (table issue du backend).
 * ──────────────────────────────────────────────────────────────────────────── */
const REPORT_TINY = 0.01;
export const REPORT_RATIO_DECIMALS = 2;

const _reportThousands = (intPart) => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const _reportFixed = (absValue, decimals) => {
  const [intPart, fracPart] = absValue.toFixed(decimals).split('.');
  return fracPart ? `${_reportThousands(intPart)}.${fracPart}` : _reportThousands(intPart);
};

export const reportTco2eDecimals = (value) => {
  const v = Math.abs(Number(value));
  if (v >= 100) return 0;
  if (v >= 10) return 1;
  if (v >= 1) return 2;
  if (v >= REPORT_TINY) return 3;
  return null;
};

export const reportGroupDecimals = (total) => {
  if (total === null || total === undefined || Number(total) === 0) return 0;
  const d = reportTco2eDecimals(total);
  return d === null ? 3 : d;
};

export const formatReportTco2e = (value, decimals) => {
  if (value === null || value === undefined) return '-';
  const v = Number(value);
  if (v === 0) return '0';
  if (Number(Math.abs(v).toFixed(decimals)) === 0) {
    const step = (10 ** -decimals).toFixed(decimals);
    return v > 0 ? `< ${step}` : `> -${step}`;
  }
  return `${v < 0 ? '-' : ''}${_reportFixed(Math.abs(v), decimals)}`;
};

export const formatReportRatio = (value) => {
  if (value === null || value === undefined) return '-';
  const v = Number(value);
  return `${v < 0 ? '-' : ''}${_reportFixed(Math.abs(v), REPORT_RATIO_DECIMALS)}`;
};
